import { CONVEYANCE_TYPES } from '@koala/sdk';
import { useState, useEffect } from 'react';
import { StyledStoreBanner, StyledStoreBannerContent } from './styles';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { DATE_FORMAT } from '@/constants/dates';
import { STORE_BANNER_DISPLAY } from '@/constants/menu';
import { PosUnavailableBanner, useStoreAvailabilityState } from '@/features/store/pos-unavailable';
import { useSelector } from '@/redux';
import { useConfigOverride } from '@/redux/cmsConfig';
import { getSanitizedOffset } from '@/utils/dates';
import { determineFulfillment } from '@/utils/fulfillment';
import {
  isStoreCurrentlyOpen,
  deriveFormattedDateTimeString,
  getNextHourSlots,
  getOperatingHoursForLocation,
  STORE_AVAILABILITY_STATE,
} from '@/utils/locations';

export const StoreBanner = () => {
  const deliveryAddress = useSelector((state) => state.app.conveyanceMode);
  const { fulfillment: basketFulfillment, location: basketLocation } = useSelector(
    (state) => state.app.basket,
  );
  const locationDetail = useSelector((state) => state.app.locations.detail);
  const { display } = useConfigOverride('store_banner');

  const [bannerVisibility, setBannerVisibility] = useState<boolean>(false);
  const [nextOpenHours, setNextOpenHours] = useState<string | null>(null);
  const [orderMode, setOrderMode] = useState<CONVEYANCE_TYPES>(CONVEYANCE_TYPES.PICKUP);

  // Resolves the single highest-priority availability state and re-renders when the POS
  // refusal window lapses (TEAM-14640).
  const availabilityState = useStoreAvailabilityState(locationDetail);

  const setBannerContent = () => {
    const newOrderMode = determineFulfillment(
      deliveryAddress,
      // @ts-expect-error: locationDetail cannot be undefined
      locationDetail,
      basketLocation,
      basketFulfillment,
    );

    if (newOrderMode !== orderMode) {
      setOrderMode(newOrderMode);
    }

    const locationHours = getOperatingHoursForLocation(locationDetail);
    const hours = getNextHourSlots(locationHours);

    const openHoursDateFormat = DATE_FORMAT.HOURS_WITH_MINUTES;
    const offset = locationDetail?.cached_data
      ? getSanitizedOffset(locationDetail.cached_data.utc_offset)
      : null;

    const openHours = deriveFormattedDateTimeString(
      hours[0]?.start,
      openHoursDateFormat,
      /** @TODO differentiate between `null` and `undefined`. */
      // @ts-expect-error
      offset,
    );
    setNextOpenHours(openHours);
    setBannerVisibility(true);
  };

  useEffect(() => {
    if (
      display === STORE_BANNER_DISPLAY.ALWAYS ||
      (display === STORE_BANNER_DISPLAY.STORE_CLOSED &&
        !isStoreCurrentlyOpen(getOperatingHoursForLocation(locationDetail)))
    ) {
      setBannerContent();
    }
  });

  // Store-unavailable (POS refusal or operator-paused/disabled) takes precedence over the
  // CMS/operating-hours banner and renders unconditionally (independent of `store_banner.display`),
  // so a diner is always warned. The hours path below is left untouched. (TEAM-14640, TEAM-15703)
  if (
    availabilityState === STORE_AVAILABILITY_STATE.POS_UNAVAILABLE ||
    availabilityState === STORE_AVAILABILITY_STATE.OPERATOR_PAUSED
  ) {
    return <PosUnavailableBanner location={locationDetail} />;
  }

  // Additionally checking nextOpenHours to keep from rendering $openHours$ while StringAccessor is executing
  if (bannerVisibility && nextOpenHours) {
    return (
      <StyledStoreBanner>
        <StyledStoreBannerContent>
          <StringAccessor
            html={true}
            accessor="store.banner"
            dataObj={{
              openHours: nextOpenHours,
              conveyanceType: orderMode,
            }}
            tag="p"
          />
        </StyledStoreBannerContent>
      </StyledStoreBanner>
    );
  }

  return null;
};
